import { FC, useMemo, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { useMutation, useQuery } from 'react-query';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { minLengthValidation, DEFAULT_NAME_MIN_LENGTH } from 'utils/patterns';
import { useS3ImageUploader } from 'hooks/useS3ImageUploader';
import { makeSelectOptions } from 'utils/makeSelectOptions';
import { TextAreaInput } from 'components/Inputs/TextAreaInput';
import SelectComponent from 'components/Inputs/SelectComponent';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { IOption } from 'components/Inputs/SelectComponent/types';
import { Button } from 'components/Core/Button';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { Switch } from 'components/Core/Switch/Switch';
import EntityOption from 'components/Core/EntityOption';
import { IBrandRef } from 'types/common';
import { DiscountType, IProductData } from 'stores/products/types';
import { productFormTranslations } from './translations';
import { ProductBasicFormData, ProductFormTransFields } from './types';
import { discountTypeOptions, PRICE_DEFAULT_MIN } from './constants';
import { getCurrencyCodeFromCountry } from 'utils/getCurrencySymbol';
import { DatePickerRow } from 'components/Inputs/DatePickerComponents/DatePickerRow';
import styles from './productBasicForm.module.scss';

export const ProductBasicForm: FC = observer(() => {
  const {
    brandsStore: { getAutoCompleteBrands },
    productsStore: {
      productsCategories,
      getProductsCategories,
      addProduct,
      selectedProduct,
      setSelectedProduct,
      editProduct,
      deleteProduct,
    },
    mainStore: { user },
    popUpStore: { openPopUp, closePopUp },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<ProductFormTransFields>([productFormTranslations.name]);
  const navigate = useNavigate();

  useEffect(() => {
    return () => setSelectedProduct(null);
  }, []);

  useQuery('getProductsCategories', getProductsCategories, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { mutate: addProductMutation, isLoading: isLoadingAdding } = useMutation(addProduct, {
    mutationKey: 'addPoint',
    onSuccess: (res: IProductData) => {
      showAlert(`create product success`, 'success');
      navigate(`/products/${res.id}`);
    },
  });

  const { mutate: editProductMutation, isLoading: isLoadingEditing } = useMutation(editProduct, {
    mutationKey: 'editProduct',
    onSuccess: () => {
      showAlert(`edit product success`, 'success');
    },
  });

  const { mutate: deleteProductMutation, isLoading: isLoadingDeletting } = useMutation(deleteProduct, {
    mutationKey: 'deleteProduct',
    onSuccess: () => {
      closePopUp();
      showAlert(`delete product success`, 'success');
      navigate(`/products`);
    },
  });

  const defaultValues = useMemo((): Partial<ProductBasicFormData> | undefined => {
    if (selectedProduct) return selectedProduct;
    if (user)
      return {
        currencyCode: getCurrencyCodeFromCountry(user.selectedCountryCode.countryCode),
      };
    return undefined;
  }, [user]);

  const useFormMethods = useForm<ProductBasicFormData>({ defaultValues, mode: 'onChange' });

  const {
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    trigger,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, isSubmitting }, // should create errors conts to update errors when they appears
  } = useFormMethods;

  /* MOB1-2259: a country switch while a NEW product is being filled in starts the form over with the new
     country's currency (the country is the only thing the form knows about the product by then). An
     existing product keeps the currency it was saved with — this effect used to overwrite it on mount,
     so a crawler's BRL product came back with the country's currency after any edit. */
  useEffect(() => {
    if (selectedProduct) return;
    reset({ currencyCode: getCurrencyCodeFromCountry(user?.selectedCountryCode.countryCode) });
  }, [user?.selectedCountryCode.countryCode]);

  const formateNumberInput = (name: keyof ProductBasicFormData, value?: string | number) => {
    const val = value ?? getValues(name);
    if (val !== undefined && val !== null) {
      const vals = val.toString().split('.');
      if (vals[1]) setValue(name, +`${vals[0]}.${vals[1].slice(0, 2)}`);
      else setValue(name, Number((+vals[0]).toFixed(2)));
    }
  };

  const setCheckedNumberInput = useCallback((name: keyof ProductBasicFormData, value: string | number) => {
    if (!Number.isNaN(+value)) {
      const vals = value.toString().split('.');
      if (vals[1]) setValue(name, +`${vals[0]}.${vals[1].slice(0, 2)}`);
      else setValue(name, value);
    }
    switch (name) {
      case 'price':
      case 'discountValue':
        setupDiscountPrice();
        break;
      case 'discountPrice':
        onChangeDiscountPrice();
        break;
      default:
        break;
    }
  }, []);

  const setupDiscountPrice = useCallback(() => {
    const p = getValues('price');
    const d = getValues('discountValue');
    if (p !== undefined && p !== null && d !== undefined && d !== null) {
      if (getValues('discountType') === 'fixed') {
        formateNumberInput('discountPrice', p - d);
      } else {
        formateNumberInput('discountPrice', Math.round(p * (1 - d / 100) * 100) / 100 || PRICE_DEFAULT_MIN);
      }
    }
    setTimeout(() => {
      trigger('price');
      trigger('discountPrice');
      trigger('discountValue');
    });
  }, []);

  const onChangeDiscountPrice = () => {
    const p = getValues('price');
    const dp = getValues('discountPrice');
    if (p !== undefined && dp !== undefined && dp !== null) {
      if (getValues('discountType') === 'fixed') {
        formateNumberInput('discountValue', p - dp);
      } else {
        formateNumberInput('discountValue', Math.round(100 * (1 - dp / p) * 100) / 100 || PRICE_DEFAULT_MIN);
      }
    }
    setTimeout(() => {
      trigger('discountPrice');
      trigger('discountValue');
    });
  };

  const onDiscountTypeSelect = useCallback((val: IOption | undefined | null) => {
    if (val) {
      setValue('discountType', val.value as DiscountType);
      setupDiscountPrice();
      setTimeout(() => {
        trigger('startDate');
        trigger('expiredDate');
      });
    } else {
      setValue('discountType', val);
      setValue('discountType', null);
      setValue('discountValue', null);
      setValue('discountPrice', null);
      setValue('startDate', null);
      setValue('expiredDate', null);
      setTimeout(() => {
        trigger('discountPrice');
        trigger('discountValue');
        trigger('startDate');
        trigger('expiredDate');
      });
    }
  }, []);

  const renderBrandOption = useCallback((data: IBrandRef) => {
    return <EntityOption image={data.logoUrl} entityType='brands' name={data.name} />;
  }, []);

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<ProductBasicFormData>({ setValue });

  const onSubmitHandler = useCallback(
    async (formData: ProductBasicFormData) => {
      const { imageUrlFile: iFile, brand } = formData;
      try {
        const path = `${user?.selectedCountryCode.countryCode.toLowerCase()}/${brand?.slug}/admin-products`;
        if (iFile) await uploadImage(iFile, 'imageUrl', path);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { imageUrlFile, ...data } = getValues();
        if (selectedProduct) {
          const newData = { ...(selectedProduct || {}), ...data };
          editProductMutation(newData);
          return;
        }

        addProductMutation(data);
      } catch {
        showAlert('Problems with image upload', 'warning');
      }
    },
    [user, selectedProduct]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.formSection}`}>
            <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
            <ControlledInput
              name='name'
              type='text'
              label={t('name')}
              required
              validation={minLengthValidation(DEFAULT_NAME_MIN_LENGTH)}
            />
            <TextAreaInput name='description' label={t('description')} />
            <AutocompleteSelect
              name='brand'
              label={t(`brand`)}
              data={[]}
              onSearch={getAutoCompleteBrands}
              customOption={renderBrandOption}
              customSingleValue={renderBrandOption}
              isDisabled={!!selectedProduct?.brand}
              required
            />
            <ControlledInput
              name='price'
              type='money'
              label={t('price')}
              icon={<p className={styles.currency}>{getValues('currencyCode')}</p>}
              iconPosition='right'
              required
              min={PRICE_DEFAULT_MIN}
              myOnChange={(val) => setCheckedNumberInput('price', val)}
            />
            <SelectComponent
              name='categories'
              label={t('categories')}
              placeholder=''
              options={productsCategories || []}
              optionLabel='categoryName'
              optionValue='id'
              maxMenuHeight={160}
              isMulti
              required
            />
            <SelectComponent
              name='discountType'
              label={t('discountType')}
              placeholder=''
              options={makeSelectOptions(discountTypeOptions)}
              onSelect={(val) => onDiscountTypeSelect(val as unknown as IOption)}
              isDisabled={!watch('price')}
            />
            <ControlledInput
              name='discountValue'
              type='money'
              label={t('discountValue')}
              icon={
                watch('discountType') ? (
                  <p className={styles.currency}>
                    {watch('discountType') === 'fixed' ? getValues('currencyCode') : '%'}
                  </p>
                ) : undefined
              }
              iconPosition='right'
              disabled={!watch('discountType')}
              required={!!watch('discountType')}
              min={PRICE_DEFAULT_MIN}
              max={(watch('discountType') === 'fixed' ? watch('price') : 100) - PRICE_DEFAULT_MIN}
              myOnChange={(val) => setCheckedNumberInput('discountValue', val)}
            />
            <ControlledInput
              name='discountPrice'
              type='money'
              label={t('discountPrice')}
              placeholder=''
              icon={watch('discountType') ? <p className={styles.currency}>{getValues('currencyCode')}</p> : undefined}
              iconPosition='right'
              disabled={!watch('discountType')}
              required={!!watch('discountType')}
              min={PRICE_DEFAULT_MIN}
              max={watch('price') - PRICE_DEFAULT_MIN}
              myOnChange={(val) => setCheckedNumberInput('discountPrice', val)}
            />
            <DatePickerRow
              startLabel={t('startDate')}
              endLabel={t('expiredDate')}
              startName='startDate'
              endName='expiredDate'
              requiredStart={!!watch('discountType')}
              requiredEnd={!!watch('discountType')}
              isDisabledStart={!watch('discountType')}
              noMaxDateStart={!watch('expiredDate')}
              noMaxDateEnd
            />
            <div className={styles.checkboxWrap}>
              <h6 className={`${styles.title}`}>{t('status')}</h6>
              <Switch
                isActive={watch('status') === 'Active'}
                onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
              />
            </div>
          </section>
          <section className={`${styles.rightSideSection}`}>
            <ImageUploader name='imageUrl' title={t('imageUrl')} path='products' required />
          </section>
        </form>
        <div className={styles.bottomSection}>
          <Button
            title={t('Save')}
            onClick={handleSubmit(onSubmitHandler)}
            isDisabled={isImageUploading || isLoadingAdding || isLoadingEditing || isLoadingDeletting || isSubmitting}
          />
          {selectedProduct && (
            <Button
              title={t('Delete')}
              onClick={() =>
                openPopUp({
                  title: t('confirmationTitle'),
                  description: t('approve-text'),
                  buttonLabel: t('confirm'),
                  formName: 'confirmation',
                  cb: () => deleteProductMutation(selectedProduct.id),
                })
              }
              isDisabled={isImageUploading || isLoadingAdding || isLoadingEditing || isLoadingDeletting}
              classNameProp={styles.deleteBtn}
            />
          )}
        </div>
      </div>
    </FormProvider>
  );
});
